import React, { useState, useEffect } from 'react';
import { BigNumberish, ethers } from 'ethers';
import { useAccount } from 'wagmi';
import { Typography, Table, Tag, Button, Space, Modal, Form, Input, Radio, Checkbox, message,Spin } from 'antd';
import DashboardLayout from '../components/DashboardLayout';
import { useWriteContract, useWaitForTransactionReceipt, useReadContract } from 'wagmi';
import { CONTRACT_ADDRESS, CONTRACT_ABI, getContractInstance } from '../config/contractConfig';
import { BASE_URL } from "../utils/URL_Config"; 
import  IERC20_ABI  from "../config/IERC20_ABI"; 
import { formatUnits } from 'ethers';
import { usePublicClient } from 'wagmi';



const { Title } = Typography;

type ProjectData = {
  key: string;
  projectId: string;
  url: string;
  amount: number;
  mode: 'Default' | 'Custom';
  customTokenAddress?: string;
  defaultTokenAddress?: string; 
  autoReward: boolean;
  status: string;
  createdAt: string;
};

const Projects: React.FC = () => {
  const [dataSource, setDataSource] = useState<ProjectData[]>([]); 
  const [isModalVisible, setIsModalVisible] = useState(false); 
  const [form] = Form.useForm(); // Form instance
  const [mode, setMode] = useState<'Default' | 'Custom'>('Default'); 
  const [isCreating, setIsCreating] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null); 
  const [isAllowanceModalVisible, setIsAllowanceModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentProject, setCurrentProject] = useState<ProjectData | null>(null);
  const [allowances, setAllowances] = useState<{ [key: string]: number }>({});
  const [newAllowance, setNewAllowance] = useState<number>(0);
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [iconUrl, setIconUrl] = useState<string | null>(null); 
  const [imageUrls, setImageUrls] = useState<string[]>([]); 
  const [isUploading, setIsUploading] = useState(false); 
  const [defaultFee, setDefaultFee] = useState<number | null>(null);
  const [customFee, setCustomFee] = useState<number | null>(null);
  // // State for Custom Token
  // const [tokenSymbol, setTokenSymbol] = useState<string | null>(null);
  // const [tokenDecimals, setTokenDecimals] = useState<number | null>(null);
  // const [customTokenAddress, setCustomTokenAddress] = useState<string | null>(null);
  // // State for Default Token
  // const [defaultTokenSymbol, setDefaultTokenSymbol] = useState<string | null>(null);
  // const [defaultTokenDecimals, setDefaultTokenDecimals] = useState<number | null>(null);

  const { writeContractAsync, isSuccess, isError, isPending } = useWriteContract();

  const { address: userAddress } = useAccount();

  const publicClient = usePublicClient();

  // Fetch projects created by the current user
  const { data: userProjects, isLoading } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'getAddressProjects',
    args: [userAddress, 0, 100], 
  });


  //fetching default token address
  const { data: defaultTokenAddress } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'defaultRewardToken',
  });

  const { data: defaultServiceFee } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'serviceFee',
  });
  
  const { data: customServiceFee } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'serviceProFee',
  });

  
  const showIncreaseAllowanceModal = (project: ProjectData) => {
    setCurrentProject(project);
    setIsAllowanceModalVisible(true);
  };
  

  useEffect(() => {
    const fetchProjectsAndAllowances = async () => {
      if (!publicClient) return;
      if (userProjects && Array.isArray(userProjects) && defaultTokenAddress) {
        // Format user projects 
        const formattedProjects = userProjects.map((project: any, index: number) => {
        const isDefaultToken = project.tokenContract === defaultTokenAddress;
  
          return {
            key: index.toString(),
            projectId: project.projectId,
            url: project.url,
            amount: parseFloat(ethers.formatUnits(project.reward, "ether")),
            mode: isDefaultToken ?  ('Default' as 'Default' | 'Custom') : ('Custom' as 'Default' | 'Custom'),
            customTokenAddress: isDefaultToken ? "N/A" : project.tokenContract,
            defaultTokenAddress: isDefaultToken ? (defaultTokenAddress as string) : "N/A",
            autoReward: project.autoReward,
            status: "Active",
            createdAt: new Date().toISOString().split("T")[0],
          };
        });
  
        setDataSource(formattedProjects);
  
        // Fetch allowances for each project
        const updatedAllowances: { [key: string]: number } = {};
  
        for (const project of userProjects) {
          const allowance = await publicClient.readContract({
            address: project.tokenContract as `0x${string}`,
            abi: IERC20_ABI,
            functionName: "allowance",
            args: [project.owner, CONTRACT_ADDRESS],
          });
  
          if (allowance) {
            updatedAllowances[project.projectId] = parseFloat(formatUnits(allowance as BigNumberish, 'ether'));
          }
        }
  
        setAllowances(updatedAllowances);
      }

      if (defaultServiceFee) {
        setDefaultFee(parseFloat(formatUnits(defaultServiceFee as BigNumberish, 'ether')));
      }
      if (customServiceFee) {
        setCustomFee(parseFloat(formatUnits(customServiceFee as BigNumberish , 'ether')));
      }
    };
  
    fetchProjectsAndAllowances();
  }, [userProjects, defaultTokenAddress, defaultServiceFee, customServiceFee]);


  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields(); 
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    form.resetFields(); 
  };

  const handleCreateProject = async (values: any) => {
    setIsCreating(true);

    try {
      const { url, amount, autoReward, customTokenAddress } = values;
      const args =
        mode === 'Default'
          ? [url, ethers.parseUnits(amount.toString(), 'ether'), autoReward]
          : [url, ethers.parseUnits(amount.toString(), 'ether'), autoReward, customTokenAddress];

      const functionName = mode === 'Default' ? 'createProjectDefaultToken' : 'createProjectCustomToken';
      const value = mode === 'Default'
      ? ethers.parseUnits((defaultServiceFee as BigNumberish).toString(), 'wei') 
      : ethers.parseUnits((customServiceFee as BigNumberish).toString(), 'wei');

      const tx = await writeContractAsync({
        abi: CONTRACT_ABI,
        address: CONTRACT_ADDRESS,
        functionName,
        args,
        value,
      });

      setTxHash(tx);

      message.success('Transaction sent to the blockchain!');
      console.log('Transaction hash:', tx);
      
      if (isPending) {
        console.log('Transaction pending:', isPending);
      }

      if (isError) {
        console.log('Transaction failed:', isError);
      }

      if (isSuccess) {
        console.log('Transaction confirmed:', isSuccess);
      }

      if(publicClient){
        const receipt = await publicClient.waitForTransactionReceipt({hash:tx});
        console.log('Transaction hash:', tx);

        const title = form.getFieldValue('title'); //from the form
        const description = form.getFieldValue('description');  //from the form

        console.log('The title is:', title);
        console.log('The description is:', description);

        const projectCreatedEvent = receipt.logs?.find((log: any) =>
          log.topics[0] === ethers.id("ProjectCreated(bytes32,uint256,bool,address,address)")
        );
  
        // Extract projectId from the event data
        if (projectCreatedEvent) {
          const abiCoder = new ethers.AbiCoder(); 
  
          // Decode the event data using the correct types
          const decodedLogs = abiCoder.decode(
            ['bytes32', 'uint256', 'bool', 'address', 'address'],
            projectCreatedEvent.data
          );
  
          const projectId = decodedLogs[0]; // The first value is projectId (bytes32)
          const projectOwner = decodedLogs[3]; //The third is project owner emitted (address)
  
          console.log('Project Created with ID:', projectId);
          console.log('Project Owner is:', projectOwner);
  
          updateProject(projectId.toString(), title, description);

          const newProject: ProjectData = {
            key: `${dataSource.length + 1}`,
            projectId: projectId?.toString() || '', // Store the projectId in the project data
            url: form.getFieldValue('url'),
            amount: Number(form.getFieldValue('amount')),
            mode,
            customTokenAddress: form.getFieldValue('customTokenAddress') || '',
            autoReward: form.getFieldValue('autoReward') || false,
            status: 'Active',
            createdAt: new Date().toISOString().split('T')[0],
            defaultTokenAddress: mode === 'Default' ? CONTRACT_ADDRESS : '', // Show Default token address if mode is Default
          };

          setDataSource([...dataSource, newProject]);
          setIsModalVisible(false);
          form.resetFields();
        }

        
      }

    } catch (error: any) {
      console.error('Error creating project:', error.message);
      message.error('Failed to create project on the blockchain.');
    } finally {
      setIsCreating(false);
    }
  };

  const updateProject = async (projectId: string, title: string, description: string) => {
    const token = localStorage.getItem('authToken');

    console.log('this is updateProject function');
    console.log('Project ID:', projectId);
    // console.log('Project Owner is:', projectOwner);
    console.log('the current user address is :', userAddress);
    
    if (!token) {
      message.error("Token not found. Please log in again.");
      return;
    }

    // if (userAddress !== projectOwner) {
    //   message.error("You are not the project owner.");
    //   return;
    // }
    if(publicClient) {
      const isOwner = await publicClient.readContract({
        address: CONTRACT_ADDRESS,
        abi: CONTRACT_ABI,
        functionName: 'checkProjectOwner',
        args: [projectId, userAddress],  // Passing projectId and userAddress to verify ownership
      });

      if (!isOwner) {
        message.error("You are not the project owner.");
        return;
      }
    } 

    // const title = form.getFieldValue('title');
    // const description = form.getFieldValue('description');

    console.log('The title is:', title);
    console.log('The description is:', description);
  
    try {
      
      const projectData = {
        token: token,
        chain: "amoy", 
        project_id: projectId, 
        title: title, 
        content: description,
        icon: iconUrl, 
        screen: imageUrls, 
      };
  
      const response = await fetch("https://mobius.hipple.io/api/v1/project/edit_project", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectData),
      });
  
      const data = await response.json();

      console.log("API Response Data:", data);
  
      if (data.error === 0) {
        message.success("Project updated successfully!");
        // Optionally, you can reload or update the project data displayed
      } else {
        message.error(`Failed to update project: ${data.message}`);
      }
    } catch (error) {
      console.error("Error updating project:", error);
      message.error("Failed to update project.");
    }
  };

  const handleEditProject = (project: ProjectData) => {
    console.log('Selected project for editing:', project); 
    setCurrentProject(project); 
    setIsEditModalVisible(true);  
  };

  const handleEditSubmit = async (values: any) => {
    if (!currentProject) {
      console.log('No current project found');
      return;
    }
    const { title, description } = values;
    console.log('Form values:', values);
  
    try {
      setIsCreating(true);
  
      // Call a function to update the project (backend or blockchain)
      await updateProject(currentProject?.projectId, title, description);
  
      // Optionally, update the project data state
      setDataSource(dataSource.map((project) =>
        project.projectId === currentProject?.projectId
          ? { ...project, title, description }
          : project
      ));
  
      message.success("Project updated successfully!");
      setIsEditModalVisible(false);  // Close the modal
      form.resetFields();  // Reset the form fields
    } catch (error) {
      console.error("Error updating project:", error);
      message.error("Failed to update project.");
    } finally {
      setIsCreating(false);
    }
  };
  

  const handleIncreaseAllowance = async () => {
    try {
      if (!currentProject) return;

      // Determine the token contract address (custom or default)
        const tokenAddress =
        currentProject.mode === 'Custom'
          ? currentProject.customTokenAddress
          : currentProject.defaultTokenAddress;

      if (!tokenAddress || tokenAddress === 'N/A') {
        message.error('Invalid token address for this project.');
        return;
      }
      
      
      const tx = await writeContractAsync({
        address: tokenAddress as `0x${string}`, 
        abi: IERC20_ABI,
        functionName: 'approve',
        args: [CONTRACT_ADDRESS, ethers.parseUnits(newAllowance.toString(), 'ether')],
      });
      
      if (isSuccess) {
        message.success('Allowance successfully increased!');
      }
      
      setIsAllowanceModalVisible(false);
    } catch (error) {
      console.error('Error increasing allowance:', error);
      message.error('Failed to increase allowance. Please try again.');
    }
  };


  const generatePromotionLink = (projectId: string) => {
    const promotionLink = `${BASE_URL}/invite-code?projectid=${projectId}`;
    navigator.clipboard.writeText(promotionLink)
      .then(() => {
        message.success('Promotion link copied to clipboard!');
      })
      .catch(() => {
        message.error('Failed to copy the promotion link. Please try again.');
      });
  };

  const handleClaimReward = async (projectId: string) => {
    try {
      const inviteeAddress = prompt('Enter the invitee address to claim reward:'); // Prompt for the invitee address
      if (!inviteeAddress) {
        message.error('Invitee address is required.');
        return;
      }
  
      const tx = await writeContractAsync({
        address: CONTRACT_ADDRESS,
        abi: CONTRACT_ABI,
        functionName: 'claimReward',
        args: [projectId, inviteeAddress], 
      });
  
      if (isPending) {
        console.log('Claim Reward pending:', isPending);
      }

      if (isError) {
        console.log('Claim Reward failed:', isError);
      }

      if (isSuccess) {
        console.log('Claim Reward Successful:', isSuccess);
      }

      message.success('Reward successfully claimed!');
    } catch (error: any) {
      console.error('Error claiming reward:', error);
      message.error('Failed to claim reward. Please try again.');
    }
  };
  
  const handleIconChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]; // Get the first file the user selects
  
    if (file) {
      try {
        setIsUploading(true); // Start the upload process
  
        const token = localStorage.getItem('authToken');
        if (!token) {
          message.error("Token not found. Please log in again.");
          return;
        }
  
      
        const iconUrl = await uploadFileToCloud(file, token);
  
        if (iconUrl) {
          setIconUrl(iconUrl);
          message.success("Icon uploaded successfully.");
        } else {
          message.error("Failed to upload icon.");
        }
      } catch (error) {
        console.error("Error uploading icon:", error);
        message.error("Error uploading icon.");
      } finally {
        setIsUploading(false); 
      }
    }
  };
  
  const handleImagesChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files; // Get all selected files
  
    if (!files) return;
  
    const token = localStorage.getItem('authToken');
    if (!token) {
      message.error("Token not found. Please log in again.");
      return;
    }
  
    setIsUploading(true); 
  
    const uploadedImageUrls: string[] = [];
    const uploadPromises = Array.from(files).map((file) =>
      uploadFileToCloud(file, token).then((url) => {
        if (url) {
          uploadedImageUrls.push(url); 
        }
      })
    );
  
    try {
      // Upload all images concurrently
      await Promise.all(uploadPromises);
  
      
      setImageUrls(uploadedImageUrls);
      message.success("Images uploaded successfully.");
    } catch (error) {
      console.error("Error uploading images:", error);
      message.error("Failed to upload some images.");
    } finally {
      setIsUploading(false); 
    }
  };
  
  const uploadFileToCloud = async (file: File, token: string): Promise<string | null> => {
    try {
      const uploadConfigResponse = await fetch("https://mobius.hipple.io/api/v1/utils/upload_config", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          mimetype: file.type, // MIME type of the file (image/png, image/jpeg, etc.)
        }),
      });
  
      const uploadConfigData = await uploadConfigResponse.json();
      console.log("API Response Data:", uploadConfigData);
  
      if (!uploadConfigData.data?.upload_url) {
        console.error("Failed to get upload URL.");
        return null;
      }
  
      const uploadUrl = uploadConfigData.data.upload_url;
      console.log("Upload URL:", uploadUrl); //  upload URL
  
      const binaryData = await file.arrayBuffer();  // Convert file to binary data

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type, // Use the correct MIME type of the file
          'Content-Length': file.size.toString(), // Size of the file
        },
        body: binaryData, // Send the binary data directly
      });
  
      // Check if the response is JSON
        const contentType = uploadResponse.headers.get('Content-Type');
        console.log("content-type: " , contentType);
        console.log("uploadResponse Raw: " , uploadResponse);
        const responseText = await uploadResponse.text();  // Extract the raw text from the response
        console.log("uploadResponse.text(): ", responseText);
    
      
      if (uploadResponse.ok) {
        const objUrl = uploadConfigData.data.obj_url;
        console.log("Uploaded Image URL (obj_url):", objUrl); //  accessible URL for the uploaded image
        return objUrl; // Return the accessible URL for the uploaded image
      } else {
        console.error("Failed to upload image.");
        return null;
      }
      
    } catch (error) {
    console.error("Error uploading file:", error);
    return null;
    }
  };

  const columns = [
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: 'Token Mode',
      dataIndex: 'mode',
      key: 'mode',
    },
    {
      title: 'Custom Token Address',
      dataIndex: 'customTokenAddress',
      key: 'customTokenAddress',
      render: (address: string, record: ProjectData) => (record.mode === 'Custom' ? address : 'N/A'),
    },
    {
      title: 'Default Token Address',
      dataIndex: 'defaultTokenAddress', 
      key: 'defaultTokenAddress',
      render:(address: string, record: ProjectData) => (record.mode === 'Default' ? address : 'N/A'),
    },
    {
      title: 'Auto Reward',
      dataIndex: 'autoReward',
      key: 'autoReward',
      render: (autoReward: boolean) =>
        autoReward ? <Tag color="green">Enabled</Tag> : <Tag color="red">Disabled</Tag>,
    },
    {
      title: 'Remaining Allowance',
      dataIndex: 'projectId',
      key: 'allowance',
      render: (projectId: string) => {
        return allowances[projectId] !== undefined
          ? `${allowances[projectId]} Tokens`
          : 'Loading...';
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'Active' ? 'green' : 'volcano'}>{status}</Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: 'Actions',
      key: 'actions', 
      render: (text: string, record: ProjectData) => (
        <Space>
          <Button type="link" onClick={() => generatePromotionLink(record.projectId)}>
            Generate Promotion Link
          </Button>
          {/* Claim Reward Button for non-auto-reward projects */}
        {!record.autoReward && (
          <Button
            type="link"
            onClick={() => handleClaimReward(record.projectId)}
          >
            Claim Reward
          </Button>
        )}
          <Button type="link" onClick={() => showIncreaseAllowanceModal(record)}>
          Increase Allowance
        </Button>
        <Button type="link" onClick={() => handleEditProject(record)}>
          Edit
      </Button>
        </Space>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <div style={{ padding: '1rem' }}>
        <Title level={3}>Projects</Title>
        <p>Manage your projects from this page. You can view, edit, and delete projects.</p>
        <Button type="primary" onClick={showModal} style={{ marginBottom: '1rem' }}>
          Create Project
        </Button>
        <Spin spinning={isUploading} tip="Uploading...">
          <Table
            dataSource={dataSource}
            columns={columns}
            bordered
            style={{ marginTop: '1rem' }}
            pagination={{ pageSize: 10 }}
          />
        </Spin>
        {/* Create Project Modal */}
        <Modal
          title="Create New Project"
          visible={isModalVisible}
          onCancel={handleCancel}
          footer={null}
        >
          <Form form={form} onFinish={handleCreateProject} layout="vertical" initialValues={{ autoReward: true }}>
            <Form.Item
              name="url"
              label="Project URL"
              rules={[{ required: true, message: 'Please enter the project URL' }]}
            >
              <Input placeholder="Enter project URL" />
            </Form.Item>
            <Form.Item
              name="amount"
              label="Amount"
              rules={[{ required: true, message: 'Please enter the project amount' }]}
            >
              <Input placeholder="Enter project amount" type="number" />
            </Form.Item>
            <Form.Item name="autoReward" valuePropName="checked">
              <Checkbox>Enable Auto Reward</Checkbox>
            </Form.Item>
            <Form.Item
              name="mode"
              label="Token Mode"
              initialValue={mode}
              rules={[{ required: true, message: 'Please select a token mode' }]}
            >
              <Radio.Group onChange={(e) => setMode(e.target.value)} value={mode}>
                <Radio value="Default">Default Token</Radio>
                <Radio value="Custom">Custom Token</Radio>
              </Radio.Group>
               {/* Display the fee for each mode */}
               {mode === 'Default' && defaultFee !== null && (
                  <p style={{ marginTop: '10px', fontStyle: 'italic' }}>
                    The fee for using a default token is {defaultFee} POL.
                  </p>
                )}
                {mode === 'Custom' && customFee !== null && (
                  <p style={{ marginTop: '10px', fontStyle: 'italic' }}>
                    The fee for using a custom token is {customFee} POL.
                  </p>
                )}
            </Form.Item>
            {mode === 'Custom' && (
              <Form.Item
                name="customTokenAddress"
                label="Custom Token Address"
                rules={[{ required: true, message: 'Please enter the custom token address' }]}
              >
                <Input placeholder="Enter custom token address" />
              </Form.Item>
            )}
  
            {/* New Fields for Title, Description, Icon, and Images */}
            <Form.Item
              name="title"
              label="Project Title"
              rules={[{ required: true, message: 'Please enter the project title' }]}
            >
              <Input placeholder="Enter project title" />
            </Form.Item>
  
            <Form.Item
              name="description"
              label="Project Description"
              rules={[{ required: true, message: 'Please enter the project description' }]}
            >
              <Input.TextArea placeholder="Enter project description" />
            </Form.Item>
  
            <Form.Item
              name="icon"
              label="Project Icon"
              rules={[{ required: true, message: 'Please upload an icon for your project' }]}
            >
              <Input type="file" onChange={handleIconChange} />
              {/* Display the uploaded icon immediately below the input field */}
              {iconUrl && (
                <div style={{ marginTop: '10px' }}>
                  <h4>Uploaded Project Icon:</h4>
                  <img 
                    src={iconUrl} 
                    alt="Uploaded Icon" 
                    style={{
                      maxWidth: '150px', 
                      maxHeight: '150px', 
                      borderRadius: '8px', 
                      border: '2px solid #ddd', 
                      objectFit: 'cover'
                    }} 
                  />
                </div>
              )}
            </Form.Item>
  
            <Form.Item
              name="images"
              label="Project Images"
            >
              <Input type="file" multiple onChange={handleImagesChange} />
              {/* Display the uploaded images immediately below the input field */}
              {imageUrls.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <h4>Uploaded Project Images:</h4>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    justifyContent: 'start'
                  }}>
                    {imageUrls.map((url, index) => (
                      <div key={index} style={{
                        width: '150px',
                        height: '150px',
                        overflow: 'hidden',
                        borderRadius: '8px',
                        border: '2px solid #ddd'
                      }}>
                        <img 
                          src={url} 
                          alt={`Uploaded Image ${index + 1}`} 
                          style={{
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover'
                          }} 
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Form.Item>
  
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={isCreating}>
                  Create
                </Button>
                <Button onClick={handleCancel}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
  
        {/* Increase Allowance Modal */}
        <Modal
          title="Increase Allowance"
          visible={isAllowanceModalVisible}
          onCancel={() => setIsAllowanceModalVisible(false)}
          onOk={handleIncreaseAllowance}
        >
          <p>Increase the allowance for the project:</p>
          <Input
            type="number"
            value={newAllowance}
            onChange={(e) => setNewAllowance(Number(e.target.value))}
            placeholder="Enter new allowance amount"
          />
        </Modal>

        {/* Edit Modal */}
        <Modal
          title="Edit Project"
          visible={isEditModalVisible}
          onCancel={handleEditCancel}
          footer={null}
        >
          <Form
            onFinish={handleEditSubmit}
            layout="vertical"
            initialValues={{
              title: title,  // bind title to the form input
              description: description,  // bind description to the form input
            }}
          >
            <Form.Item
              name="title"
              label="Project Title"
              rules={[{ required: true, message: 'Please enter the project title' }]}
            >
              <Input
                placeholder="Enter project title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}  // Set the title value
              />
            </Form.Item>

            <Form.Item
              name="description"
              label="Project Description"
              rules={[{ required: true, message: 'Please enter the project description' }]}
            >
              <Input.TextArea
                placeholder="Enter project description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}  // Set the description value
              />
            </Form.Item>

            <Form.Item
              name="icon"
              label="Project Icon"
            >
              <Input type="file" onChange={handleIconChange} />
              {iconUrl && (
                <div style={{ marginTop: '10px' }}>
                  <h4>Uploaded Project Icon:</h4>
                  <img 
                    src={iconUrl} 
                    alt="Uploaded Icon" 
                    style={{
                      maxWidth: '150px', 
                      maxHeight: '150px', 
                      borderRadius: '8px', 
                      border: '2px solid #ddd', 
                      objectFit: 'cover'
                    }} 
                  />
                </div>
              )}
            </Form.Item>

            <Form.Item
              name="images"
              label="Project Images"
            >
              <Input type="file" multiple onChange={handleImagesChange} />
              {imageUrls.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <h4>Uploaded Project Images:</h4>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    justifyContent: 'start'
                  }}>
                    {imageUrls.map((url, index) => (
                      <div key={index} style={{
                        width: '150px',
                        height: '150px',
                        overflow: 'hidden',
                        borderRadius: '8px',
                        border: '2px solid #ddd'
                      }}>
                        <img 
                          src={url} 
                          alt={`Uploaded Image ${index + 1}`} 
                          style={{
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover'
                          }} 
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={isCreating}>
                  Update
                </Button>
                <Button onClick={handleEditCancel}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </DashboardLayout>
  );  
  
};

export default Projects;